/**
 * Avatar circular con iniciales del usuario.
 *
 * Sprint 11 v2 S11-V2-02. Reemplaza al `lib/layout/Avatar.tsx` de
 * S11-01 (que usaba la versión claro). Las variantes vienen del
 * handoff: `.cb-avatar` + `.cb-avatar--{titular,residente,header,pending}`.
 *
 * Reglas de derivación de iniciales:
 * - "Andrés Bravo" → "AB" (primera letra de las primeras 2 palabras).
 * - "Pedro S. Rojas" → "PR" (ignora puntos / partículas de 1 char).
 * - "Andrés" (un solo nombre) → "A".
 * - "" o falsy → "?" como fallback defensivo (no debería ocurrir porque
 *   `Persona.nombre_completo` es NOT NULL en el modelo, pero protege la UI).
 */
import type { CSSProperties } from 'react';

type AvatarSize = 'sm' | 'md' | 'lg';
type AvatarVariant = 'titular' | 'residente' | 'header' | 'pending';

interface AvatarProps {
  /** Si se pasa, el componente deriva las iniciales automáticamente. */
  nombreCompleto?: string;
  /** Iniciales explícitas; si se pasan junto a `nombreCompleto`, ganan estas. */
  iniciales?: string;
  size?: AvatarSize;
  variant?: AvatarVariant;
  /** className adicional para casos puntuales (ej: integración en filas). */
  className?: string;
  style?: CSSProperties;
}

export function derivarIniciales(nombreCompleto: string): string {
  const trimmed = (nombreCompleto || '').trim();
  if (!trimmed) return '?';

  const palabras = trimmed
    .split(/\s+/)
    .filter((p) => p.replace(/[^a-záéíóúñü]/gi, '').length >= 2);

  if (palabras.length === 0) {
    return trimmed[0]!.toUpperCase();
  }
  if (palabras.length === 1) {
    return palabras[0]![0]!.toUpperCase();
  }
  return (palabras[0]![0]! + palabras[1]![0]!).toUpperCase();
}

const SIZE_CLASSES: Record<AvatarSize, string> = {
  sm: 'w-[22px] h-[22px] text-xs',
  md: 'w-7 h-7 text-sm', // 28px
  lg: 'w-9 h-9 text-md', // 36px
};

// Variante "header" del handoff = 26px (entre sm y md). La aplicamos
// directo en el className porque es un tamaño único de la variante.
const VARIANT_CLASSES: Record<AvatarVariant, string> = {
  titular: 'bg-orange text-white',
  residente: 'bg-orange/20 text-orange',
  header: 'bg-white text-orange w-[26px] h-[26px] text-sm',
  pending: 'bg-orange/10 text-orange border border-dashed border-orange opacity-65',
};

export function Avatar({
  nombreCompleto,
  iniciales,
  size = 'md',
  variant = 'titular',
  className = '',
  style,
}: AvatarProps) {
  const txt = iniciales ?? derivarIniciales(nombreCompleto ?? '');
  const sizeClass = variant === 'header' ? '' : SIZE_CLASSES[size];
  const variantClass = VARIANT_CLASSES[variant];

  return (
    <div
      className={`rounded-full flex items-center justify-center font-medium flex-shrink-0 ${sizeClass} ${variantClass} ${className}`.trim()}
      style={style}
      aria-label={nombreCompleto ? `Avatar de ${nombreCompleto}` : undefined}
    >
      {txt}
    </div>
  );
}
